import React, { useEffect } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import ReactionThreadCompact from './ReactionThreadCompact';
import ReactionThreadDesktop from './ReactionThreadDesktop';
import ReactionThreadPopOver from './ReactionThreadPopOver';
import { usePostLayoutSize } from '../../utils/responsive/PostLayoutSizeProvider';

const POST_FRAGMENT = gql(`
  fragment ReactionThread_post on RootPostFields {
    ...ReactionThreadDesktop_post
    ...ReactionThreadPopOver_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ReactionThread_thread on PublicThreadFields {
    id
    ...ReactionThreadCompact_thread
    ...ReactionThreadDesktop_thread
    ...ReactionThreadPopOver_thread
  }
`);

type ReactionThreadProps = {
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  isFocused?: boolean;
  isPopOverOpen?: boolean;
  onResize: () => void;
  size: MagneticWallItemSize;
};

const ReactionThread = ({
  post,
  thread,
  isFocused = false,
  isPopOverOpen = false,
  onResize,
  size,
}: ReactionThreadProps) => {
  const postLayoutSize = usePostLayoutSize();
  const dispatch = useAppDispatch();

  useEffect(() => {
    onResize?.();
  }, [onResize]);

  const handleClick = () => {
    if (!isPopOverOpen) {
      dispatch(
        EditorActions.focusReaction({
          id: thread.id,
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  if (size !== 'full' && ['mobile', 'tablet'].includes(postLayoutSize)) {
    return (
      <MagneticWallItem
        id={`reaction-${thread.id}`}
        focused={isFocused}
        isPopOverOpen={isPopOverOpen}
        popOverContent={
          <ReactionThreadPopOver
            thread={thread}
            post={post}
            expanded={isFocused}
          />
        }
        onClick={handleClick}
        onBlur={handleBlur}
        compactView={
          <ReactionThreadCompact focused={isFocused} thread={thread} />
        }
        size="compact"
      />
    );
  }

  return (
    <ReactionThreadDesktop
      thread={thread}
      post={post}
      expanded={isFocused}
      size={size}
    />
  );
};

export default ReactionThread;
